import {
  call, put, select, takeLatest, takeEvery, all,
} from 'redux-saga/effects';
import callAdapter, { callAdapterResponseError } from '@/utils/sagaHelper';
import findResult, {
  findWithReadStatus,
  findWithCodes,
  successAndErrorsOnDestroy,
  wildcardFilter,
  findSelected,
} from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import selectors from '@/selectors';
import DatasetsService from '@/datasets/service';
import DictionariesService from '@/datasets/dictionaries/service';
import CohortsService from '../service';
import {
  showCohorts,
  showCohortsError,
  showCohort,
  showCohortError,
  newCohort,
  newCohortError,
  editCohortError,
  destroyCohort,
  destroyCohortError,
  datasetToCohort,
  showCohortDatasets,
  showCohortDictionaries,
  editCohortSuccess,
} from '../actions';
import actions, { cohortLists } from '../constants';

export function* cohorts(action) {
  const items = yield select(selectors.cohorts);
  const {
    display, filter, pageSize, currentPage, privateFlag, internalFlag, requestableFlag,
  } = items[action.category];
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const includePrivate = action.privateFlag || privateFlag;
  const includeInternal = action.internalFlag || internalFlag;
  const { response, error } = yield call(callAdapter, CohortsService.cohorts(
    wildcardFilter(filter),
    includePrivate,
    includeInternal,
    page,
    display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
    requestableFlag,
  ));

  if (error) {
    yield put(showCohortsError(action.category, error));
  } else {
    yield put(showCohorts(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* cohort({ code, category, isNew }) {
  if (isNew) {
    yield put(showCohort(category, code, { code }, true));
  } else {
    const { response, error } = yield call(callAdapter, CohortsService.cohort(code));
    if (error) {
      yield put(showCohortError(category, code, error));
    } else {
      yield put(showCohort(category, code, response, false));
    }
  }
}

export function* create() {
  const item = yield select(selectors.activeCohort);
  const { response, error } = yield call(callAdapter, CohortsService.create(item));

  if (error) {
    yield put(newCohortError(error));
  } else {
    const items = yield select(selectors.cohorts);
    const toRefresh = findWithReadStatus(items, cohortLists, states.SUCCESS);

    yield put(newCohort('open', item.uuid, response));
    yield all(toRefresh.map((category) => cohorts({ category })));
  }
}

export function* destroy(action) {
  const results = yield all(action.items.map((i) => callAdapterResponseError(i, CohortsService.destroy(i))));
  const { success, errors } = successAndErrorsOnDestroy(results, 'Cohort not found');

  if (errors.length > 0) {
    yield put(destroyCohortError(errors));
  }
  if (success.length > 0) {
    const items = yield select(selectors.cohorts);
    const codes = success.map((item) => item.code);
    const categories = findWithCodes(items, cohortLists, codes);
    const toRefresh = findWithReadStatus(items, categories, states.SUCCESS);

    yield put(destroyCohort(success, false));
    yield all(toRefresh.map((category) => cohorts({ category })));
  }
}

export function* update({ code, properties }) {
  const { response, error } = yield call(callAdapter, CohortsService.update(code, properties));
  if (error) {
    yield put(editCohortError('open', code, error));
  } else {
    yield put(editCohortSuccess(code, response));
  }
}

export function* dataset({
  category,
  code,
  datasetCode,
  dictionaryCodes,
}) {
  const { response, error } = yield call(callAdapter, DatasetsService.dataset(datasetCode));
  if (error) {
    yield put(showCohortError(category, code, error));
  } else {
    yield put(datasetToCohort(code, category, response, dictionaryCodes));
  }
}

export function* datasets(action) {
  const items = yield select(selectors.cohorts);
  const item = findResult(items, action.category, action.code);
  const {
    filter, pageSize, currentPage, privateFlag, internalFlag, publicFlag, dataModelCode, orderBy,
  } = item.datasets;
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const includePrivate = action.privateFlag || privateFlag;
  const includeInternal = action.internalFlag || internalFlag;
  const includePublic = action.publicFlag || publicFlag;
  const includeDataModel = action.dataModelCode || dataModelCode;
  const { response, error } = yield call(callAdapter, DatasetsService.datasets(
    wildcardFilter(filter),
    includePrivate,
    includeInternal,
    includePublic,
    includeDataModel,
    page,
    size,
    false,
    true,
    orderBy,
  ));

  if (error) {
    yield put(showCohortError(action.category, action.code, error));
  } else {
    yield put(showCohortDatasets(
      action.category,
      action.code,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* dictionaries(action) {
  const items = yield select(selectors.cohorts);
  const item = findResult(items, action.category, action.code);
  const selectedDatasets = findSelected(item.datasets.results);
  const datasetCode = selectedDatasets.length > 0 ? selectedDatasets[0].code : null;
  const { filter, pageSize, currentPage } = item.dictionaries;
  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;

  if (datasetCode) {
    const { response, error } = yield call(callAdapter, DictionariesService.datasetDictionaries(
      wildcardFilter(filter),
      datasetCode,
      true,
      page,
      size,
    ));

    if (error) {
      yield put(showCohortError(action.category, action.code, error));
    } else {
      yield put(showCohortDictionaries(
        action.category,
        action.code,
        action.append,
        size,
        response.items,
        response.paging.total,
        response.paging.pages,
        response.paging.page,
      ));
    }
  } else {
    yield put(showCohortDictionaries(action.category, action.code, false, size, [], 0, 0, 0));
  }
}

export function* refreshCohortsPresentation(action) {
  yield call(cohorts, { ...action, page: 1, append: false });
}

export default function* cohortApi() {
  yield takeEvery([actions.FETCH_COHORTS, actions.REFRESH_COHORTS, actions.UPDATE_COHORTS_FLAGS], cohorts);
  yield takeEvery(actions.FETCH_COHORT, cohort);
  yield takeLatest(actions.CREATE_COHORT, create);
  yield takeEvery(actions.UPDATE_COHORT, update);
  yield takeEvery(actions.DELETE_COHORT, destroy);
  yield takeEvery(actions.NEW_COHORT_FROM_DATASET, dataset);
  yield takeEvery([actions.FETCH_COHORT_DATASETS, actions.UPDATE_COHORT_DATASETS_FLAGS], datasets);
  yield takeEvery([actions.TOGGLE_COHORT_DATASET_SELECTED, actions.FETCH_COHORT_DICTIONARIES], dictionaries);
  yield takeEvery(actions.TOGGLE_COHORTS_PRESENTATION, refreshCohortsPresentation);
}
